/**
 * LIHL Collectible Card canvas renderer.
 *
 * Draws the card FACE (and back) onto a 2D canvas at 1024x1434 (5:7).
 * This is the single source of truth for the card design: the admin mint
 * preview (admin-ui/src/lib/cardCanvas.js is a copied ES-module build of this
 * file — keep them in sync) and the website's three.js card textures both
 * render through it, so what you mint is exactly what gets pulled.
 *
 * Exposes window.CardCanvas = { W, H, RARITY, renderFace, renderBack }.
 * card = { name, description, flavor_text, rarity, image (HTMLImageElement|null),
 *          tagged_name, mint_number }
 *
 * opts.pixelRatio scales the backing store (1 -> 1024x1434). For canvases
 * shown in the DOM, multiply your layout ratio by devicePixelRatio so the
 * card stays sharp on retina screens; CSS controls the display size.
 *
 * Rendering is fully deterministic — no Math.random — so the same card
 * pixels come out on every render at every scale.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) { module.exports = factory(); }
  else { root.CardCanvas = factory(); }
}(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var W = 1024, H = 1434;

  var RARITY = {
    common:    { label: 'Common',    color: '#aebfd1', dark: '#5c6f83', glow: 'rgba(174,191,209,0.55)', metal: ['#e8eef5', '#8fa3b8', '#cfd9e4', '#6d8095', '#dbe4ee'],
                 tier: 0, foil: null },
    uncommon:  { label: 'Uncommon',  color: '#3ddc84', dark: '#137a45', glow: 'rgba(61,220,132,0.55)',  metal: ['#b8f5d4', '#1f9d5c', '#7cebae', '#0e6b3c', '#a3f0c6'],
                 tier: 1, foil: null },
    rare:      { label: 'Rare',      color: '#37a5ff', dark: '#0d5799', glow: 'rgba(55,165,255,0.6)',   metal: ['#bfe1ff', '#1a78d1', '#7cc3ff', '#0b4f8f', '#a8d7ff'],
                 tier: 2, foil: ['rgba(64,224,255,0.20)', 'rgba(120,140,255,0.16)', 'rgba(64,224,255,0.20)'] },
    epic:      { label: 'Epic',      color: '#b06cff', dark: '#5d23a8', glow: 'rgba(176,108,255,0.6)',  metal: ['#e2ccff', '#8138e0', '#c79bff', '#4f1c96', '#d7bcff'],
                 tier: 3, foil: ['rgba(255,96,214,0.20)', 'rgba(128,108,255,0.18)', 'rgba(64,224,255,0.16)'] },
    legendary: { label: 'Legendary', color: '#ffc233', dark: '#9a6a00', glow: 'rgba(255,194,51,0.65)',  metal: ['#fff0c2', '#e09a12', '#ffd977', '#8f6200', '#ffe9a8'],
                 tier: 4, foil: ['rgba(255,224,130,0.26)', 'rgba(255,255,255,0.20)', 'rgba(255,150,64,0.18)'] },
    mythical:  { label: 'Mythical',  color: '#ff4d4d', dark: '#8f1010', glow: 'rgba(255,77,77,0.7)',    metal: ['#ffd3cc', '#e02818', '#ff8a7a', '#8f0f08', '#ffc4ba'],
                 tier: 5, foil: ['rgba(255,90,80,0.30)', 'rgba(255,255,255,0.22)', 'rgba(255,160,64,0.20)'] }
  };

  function roundRectPath(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
  }

  // Rectangle with 45° cut corners (chamfered plate shape)
  function chamferRectPath(ctx, x, y, w, h, c) {
    ctx.beginPath();
    ctx.moveTo(x + c, y);
    ctx.lineTo(x + w - c, y);
    ctx.lineTo(x + w, y + c);
    ctx.lineTo(x + w, y + h - c);
    ctx.lineTo(x + w - c, y + h);
    ctx.lineTo(x + c, y + h);
    ctx.lineTo(x, y + h - c);
    ctx.lineTo(x, y + c);
    ctx.closePath();
  }

  function metalGradient(ctx, stops, x0, y0, x1, y1) {
    var g = ctx.createLinearGradient(x0, y0, x1, y1);
    for (var i = 0; i < stops.length; i++) {
      g.addColorStop(i / (stops.length - 1), stops[i]);
    }
    return g;
  }

  function wrapText(ctx, text, maxWidth) {
    var words = String(text || '').split(/\s+/).filter(Boolean);
    var lines = [], line = '';
    for (var i = 0; i < words.length; i++) {
      var probe = line ? line + ' ' + words[i] : words[i];
      if (ctx.measureText(probe).width > maxWidth && line) {
        lines.push(line);
        line = words[i];
      } else {
        line = probe;
      }
    }
    if (line) lines.push(line);
    return lines;
  }

  // Shrink font size until the text fits maxWidth (single line)
  function fitFont(ctx, text, weight, startPx, minPx, maxWidth, family) {
    var px = startPx;
    while (px > minPx) {
      ctx.font = weight + ' ' + px + 'px ' + family;
      if (ctx.measureText(text).width <= maxWidth) break;
      px -= 2;
    }
    return px;
  }

  // Tracked-out type reads more "premium"; harmless no-op where unsupported.
  function setSpacing(ctx, px) {
    if ('letterSpacing' in ctx) ctx.letterSpacing = px + 'px';
  }

  // Deterministic PRNG (mulberry32) so texture/sparkles never shimmer
  // between re-renders of the same card.
  function prng(seed) {
    var a = seed >>> 0;
    return function () {
      a |= 0; a = (a + 0x6D2B79F5) | 0;
      var t = Math.imul(a ^ (a >>> 15), 1 | a);
      t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  // Cached fine-grain noise tile — breaks up gradient banding on the body.
  var _noiseTile = null;
  function noisePattern(ctx) {
    if (!_noiseTile) {
      var t = (typeof document !== 'undefined') ? document.createElement('canvas') : null;
      if (!t) return null;
      t.width = t.height = 128;
      var tc = t.getContext('2d');
      var img = tc.createImageData(128, 128);
      var rand = prng(0x11895);
      for (var i = 0; i < img.data.length; i += 4) {
        img.data[i] = img.data[i + 1] = img.data[i + 2] = 255;
        img.data[i + 3] = (rand() * 14) | 0;
      }
      tc.putImageData(img, 0, 0);
      _noiseTile = t;
    }
    return ctx.createPattern(_noiseTile, 'repeat');
  }

  // Four-point sparkle star
  function drawSparkle(ctx, x, y, r, alpha) {
    ctx.save();
    ctx.globalAlpha = alpha;
    var g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, '#ffffff');
    g.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = g;
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.quadraticCurveTo(x + r * 0.12, y - r * 0.12, x + r, y);
    ctx.quadraticCurveTo(x + r * 0.12, y + r * 0.12, x, y + r);
    ctx.quadraticCurveTo(x - r * 0.12, y + r * 0.12, x - r, y);
    ctx.quadraticCurveTo(x - r * 0.12, y - r * 0.12, x, y - r);
    ctx.fill();
    ctx.restore();
  }

  function drawDiamond(ctx, x, y, r, fill) {
    ctx.beginPath();
    ctx.moveTo(x, y - r);
    ctx.lineTo(x + r, y);
    ctx.lineTo(x, y + r);
    ctx.lineTo(x - r, y);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
  }

  // ---------- frame ----------

  function drawFrame(ctx, R, pad) {
    // Base metal
    roundRectPath(ctx, 0, 0, W, H, 56);
    ctx.fillStyle = metalGradient(ctx, R.metal, 0, 0, W, H);
    ctx.fill();

    // Brushed-metal grain + foil, clipped to the frame ring only
    ctx.save();
    roundRectPath(ctx, 0, 0, W, H, 56);
    roundRectPath(ctx, pad, pad, W - pad * 2, H - pad * 2, 36);
    ctx.clip('evenodd');

    ctx.lineWidth = 1;
    for (var y = 0; y < H; y += 5) {
      ctx.strokeStyle = (y % 10) ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)';
      ctx.beginPath();
      ctx.moveTo(0, y + 0.5);
      ctx.lineTo(W, y + 0.5);
      ctx.stroke();
    }

    // Holographic foil sweep for rare and up
    if (R.foil) {
      var foil = ctx.createLinearGradient(0, H, W, 0);
      foil.addColorStop(0, 'rgba(255,255,255,0)');
      foil.addColorStop(0.22, R.foil[0]);
      foil.addColorStop(0.42, 'rgba(255,255,255,0)');
      foil.addColorStop(0.55, R.foil[1]);
      foil.addColorStop(0.72, 'rgba(255,255,255,0)');
      foil.addColorStop(0.88, R.foil[2]);
      foil.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = foil;
      ctx.fillRect(0, 0, W, H);
    }

    // Polished corner glints
    [[0, 0], [W, 0], [0, H], [W, H]].forEach(function (pt) {
      var glint = ctx.createRadialGradient(pt[0], pt[1], 0, pt[0], pt[1], 130);
      glint.addColorStop(0, 'rgba(255,255,255,0.38)');
      glint.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = glint;
      ctx.fillRect(pt[0] - 130, pt[1] - 130, 260, 260);
    });
    ctx.restore();

    // Frame edge highlight
    roundRectPath(ctx, 5, 5, W - 10, H - 10, 51);
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 2.5;
    ctx.stroke();
    // Dark seam where the frame meets the body
    roundRectPath(ctx, pad - 2, pad - 2, W - (pad - 2) * 2, H - (pad - 2) * 2, 38);
    ctx.strokeStyle = 'rgba(0,0,0,0.5)';
    ctx.lineWidth = 3;
    ctx.stroke();
  }

  // ---------- gem ----------

  // Vertex angles for each gem shape (used for outline + facet spokes)
  function gemAngles(rarity) {
    var i, out = [];
    if (rarity === 'uncommon') {
      for (i = 0; i < 3; i++) out.push(-Math.PI / 2 + i * (Math.PI * 2 / 3));
    } else if (rarity === 'epic') {
      for (i = 0; i < 5; i++) out.push(-Math.PI / 2 + i * (Math.PI * 2 / 5));
    } else if (rarity === 'common' || rarity === 'rare') {
      for (i = 0; i < 8; i++) out.push(-Math.PI / 2 + i * (Math.PI / 4));
    }
    return out;
  }

  function gemPath(ctx, cx, cy, r, rarity) {
    var i, a, pts;
    ctx.beginPath();
    if (rarity === 'common') {
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
    } else if (rarity === 'uncommon') {
      for (i = 0; i < 3; i++) {
        a = -Math.PI / 2 + i * (Math.PI * 2 / 3);
        i === 0 ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a))
                : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      ctx.closePath();
    } else if (rarity === 'rare') {
      ctx.moveTo(cx, cy - r);
      ctx.lineTo(cx + r * 0.72, cy);
      ctx.lineTo(cx, cy + r);
      ctx.lineTo(cx - r * 0.72, cy);
      ctx.closePath();
    } else if (rarity === 'epic') {
      for (i = 0; i < 5; i++) {
        a = -Math.PI / 2 + i * (Math.PI * 2 / 5);
        i === 0 ? ctx.moveTo(cx + r * Math.cos(a), cy + r * Math.sin(a))
                : ctx.lineTo(cx + r * Math.cos(a), cy + r * Math.sin(a));
      }
      ctx.closePath();
    } else { // legendary: 5-point star
      pts = 10;
      for (i = 0; i < pts; i++) {
        a = -Math.PI / 2 + i * (Math.PI / 5);
        var rr = (i % 2 === 0) ? r : r * 0.45;
        i === 0 ? ctx.moveTo(cx + rr * Math.cos(a), cy + rr * Math.sin(a))
                : ctx.lineTo(cx + rr * Math.cos(a), cy + rr * Math.sin(a));
      }
      ctx.closePath();
    }
  }

  function drawGem(ctx, cx, cy, r, rarity) {
    var R = RARITY[rarity] || RARITY.common;

    // Halo
    var halo = ctx.createRadialGradient(cx, cy, r * 0.2, cx, cy, r * 2.3);
    halo.addColorStop(0, R.glow);
    halo.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(cx, cy, r * 2.3, 0, Math.PI * 2);
    ctx.fill();

    // Metal bezel
    ctx.save();
    gemPath(ctx, cx, cy, r * 1.24, rarity);
    ctx.fillStyle = metalGradient(ctx, R.metal, cx - r, cy - r, cx + r, cy + r);
    ctx.shadowColor = 'rgba(0,0,0,0.55)';
    ctx.shadowBlur = 18;
    ctx.shadowOffsetY = 6;
    ctx.fill();
    ctx.restore();
    // Bezel rim
    gemPath(ctx, cx, cy, r * 1.24, rarity);
    ctx.strokeStyle = 'rgba(0,0,0,0.4)';
    ctx.lineWidth = 2;
    ctx.stroke();
    gemPath(ctx, cx, cy, r * 1.1, rarity);
    ctx.strokeStyle = 'rgba(255,255,255,0.35)';
    ctx.lineWidth = 1.5;
    ctx.stroke();

    // Stone body
    var body = ctx.createRadialGradient(cx - r * 0.35, cy - r * 0.4, r * 0.1, cx, cy, r * 1.15);
    body.addColorStop(0, '#ffffff');
    body.addColorStop(0.22, R.color);
    body.addColorStop(0.75, R.dark);
    body.addColorStop(1, '#06121f');
    gemPath(ctx, cx, cy, r, rarity);
    ctx.fillStyle = body;
    ctx.fill();

    // Facets: spokes to the actual vertices + inner "table" outline
    ctx.save();
    gemPath(ctx, cx, cy, r, rarity);
    ctx.clip();
    ctx.strokeStyle = 'rgba(255,255,255,0.26)';
    ctx.lineWidth = 2;
    var angles = gemAngles(rarity);
    if (rarity === 'legendary' || rarity === 'mythical') {
      for (var s = 0; s < 5; s++) angles.push(-Math.PI / 2 + s * (Math.PI * 2 / 5));
    }
    for (var i = 0; i < angles.length; i++) {
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(angles[i]) * r, cy + Math.sin(angles[i]) * r);
      ctx.stroke();
    }
    gemPath(ctx, cx, cy, r * 0.55, rarity);
    ctx.strokeStyle = 'rgba(255,255,255,0.18)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Sparkle highlight
    var hl = ctx.createRadialGradient(cx - r * 0.4, cy - r * 0.45, 0, cx - r * 0.4, cy - r * 0.45, r * 0.7);
    hl.addColorStop(0, 'rgba(255,255,255,0.9)');
    hl.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = hl;
    ctx.fillRect(cx - r, cy - r, r * 2, r * 2);
    ctx.restore();

    // Loose sparkles around the gem for the top tiers
    if (R.tier >= 3) {
      drawSparkle(ctx, cx - r * 1.75, cy - r * 0.7, 14, 0.9);
      drawSparkle(ctx, cx + r * 1.6, cy - r * 0.95, 10, 0.8);
      drawSparkle(ctx, cx + r * 1.3, cy + r * 0.85, 8, 0.7);
      if (R.tier >= 4) drawSparkle(ctx, cx - r * 1.35, cy + r * 1.0, 9, 0.75);
    }
  }

  // ---------- gem artwork ----------

  // Rarity gems are rendered artwork (assets/gem-<rarity>.png). A face
  // rendered before its gem loads gets the procedural gem and is queued
  // for a re-render when the artwork lands.
  var GEM_ART_BASE = 'assets/gem-';
  var gemArt = {};      // rarity -> { state: 'loading'|'ok'|'error', img }
  var gemPending = [];  // [{ canvas, card, opts }]

  function gemRarityOf(card) {
    return RARITY[card.rarity] ? card.rarity : 'common';
  }

  function loadGemArt(rarity) {
    if (gemArt[rarity] || typeof Image === 'undefined') return;
    var entry = gemArt[rarity] = { state: 'loading', img: null };
    var img = new Image();
    img.onload = function () {
      entry.state = 'ok';
      entry.img = img;
      var jobs = gemPending;
      gemPending = [];
      jobs.forEach(function (job) {
        if (gemRarityOf(job.card) === rarity) renderFace(job.canvas, job.card, job.opts);
        else gemPending.push(job);
      });
    };
    img.onerror = function () {
      entry.state = 'error'; // procedural gem stays as the fallback
      gemPending = gemPending.filter(function (job) { return gemRarityOf(job.card) !== rarity; });
    };
    img.src = GEM_ART_BASE + rarity + '.png';
  }

  // ---------- face ----------

  /**
   * opts: { pixelRatio (default 1 -> canvas sized to W x H) }
   */
  function renderFace(canvas, card, opts) {
    opts = opts || {};
    var scale = opts.pixelRatio || 1;
    canvas.width = W * scale;
    canvas.height = H * scale;
    var ctx = canvas.getContext('2d');
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.imageSmoothingQuality = 'high';

    var rarity = RARITY[card.rarity] ? card.rarity : 'common';
    var R = RARITY[rarity];
    var family = "'Roboto', 'Helvetica Neue', Arial, sans-serif";

    ctx.clearRect(0, 0, W, H);

    var pad = 30;
    drawFrame(ctx, R, pad);

    // ---- Body ----
    roundRectPath(ctx, pad, pad, W - pad * 2, H - pad * 2, 36);
    var body = ctx.createLinearGradient(0, pad, 0, H - pad);
    body.addColorStop(0, '#0e2440');
    body.addColorStop(0.45, '#0a1a2e');
    body.addColorStop(1, '#071120');
    ctx.fillStyle = body;
    ctx.fill();

    ctx.save();
    roundRectPath(ctx, pad, pad, W - pad * 2, H - pad * 2, 36);
    ctx.clip();

    // Rarity aura behind the name plate
    var aura = ctx.createRadialGradient(W / 2, 120, 20, W / 2, 120, 460);
    aura.addColorStop(0, R.glow.replace(/[\d.]+\)$/, '0.16)'));
    aura.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = aura;
    ctx.fillRect(0, 0, W, 600);

    // Fine diagonal pinstripes
    ctx.strokeStyle = 'rgba(150,190,230,0.045)';
    ctx.lineWidth = 1;
    for (var px = -H; px < W; px += 18) {
      ctx.beginPath();
      ctx.moveTo(px, 0);
      ctx.lineTo(px + H, H);
      ctx.stroke();
    }

    // Grain to kill gradient banding
    var np = noisePattern(ctx);
    if (np) {
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = np;
      ctx.fillRect(pad, pad, W - pad * 2, H - pad * 2);
      ctx.globalAlpha = 1;
    }

    // Light streaks across the body for the top tiers
    if (R.tier >= 3) {
      ctx.save();
      ctx.translate(W / 2, H / 2);
      ctx.rotate(-0.35);
      var streak = ctx.createLinearGradient(-90, 0, 90, 0);
      streak.addColorStop(0, 'rgba(255,255,255,0)');
      streak.addColorStop(0.5, R.tier >= 4 ? 'rgba(255,230,160,0.06)' : 'rgba(255,255,255,0.05)');
      streak.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = streak;
      ctx.fillRect(-330, -H, 180, H * 2);
      ctx.fillRect(160, -H, 110, H * 2);
      ctx.restore();
    }

    // Bottom vignette
    var bv = ctx.createLinearGradient(0, H - 340, 0, H - pad);
    bv.addColorStop(0, 'rgba(0,0,0,0)');
    bv.addColorStop(1, 'rgba(0,0,0,0.26)');
    ctx.fillStyle = bv;
    ctx.fillRect(pad, H - 340, W - pad * 2, 340 - pad);
    ctx.restore();

    // Rarity keyline just inside the body
    roundRectPath(ctx, pad + 7, pad + 7, W - (pad + 7) * 2, H - (pad + 7) * 2, 30);
    ctx.strokeStyle = R.color;
    ctx.globalAlpha = 0.5;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.globalAlpha = 1;

    // ---- Name plate (chamfered) ----
    var nameY = 64, nameH = 96, plateX = 62, plateW = W - 124;
    ctx.save();
    chamferRectPath(ctx, plateX, nameY, plateW, nameH, 22);
    var plate = ctx.createLinearGradient(0, nameY, 0, nameY + nameH);
    plate.addColorStop(0, 'rgba(20,40,62,0.95)');
    plate.addColorStop(0.5, 'rgba(10,22,36,0.95)');
    plate.addColorStop(1, 'rgba(16,32,50,0.95)');
    ctx.fillStyle = plate;
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = 16;
    ctx.shadowOffsetY = 5;
    ctx.fill();
    ctx.restore();
    chamferRectPath(ctx, plateX, nameY, plateW, nameH, 22);
    ctx.strokeStyle = metalGradient(ctx, R.metal, plateX, nameY, plateX + plateW, nameY + nameH);
    ctx.lineWidth = 3;
    ctx.stroke();
    chamferRectPath(ctx, plateX + 5, nameY + 5, plateW - 10, nameH - 10, 18);
    ctx.strokeStyle = 'rgba(255,255,255,0.10)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Studs at the plate's side points
    drawDiamond(ctx, plateX - 14, nameY + nameH / 2, 7, R.color);
    drawDiamond(ctx, plateX + plateW + 14, nameY + nameH / 2, 7, R.color);

    var name = (card.name || 'Untitled').toUpperCase();
    setSpacing(ctx, 3);
    var namePx = fitFont(ctx, name, '900', 56, 26, plateW - 76, family);
    ctx.font = '900 ' + namePx + 'px ' + family;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var ink = ctx.createLinearGradient(0, nameY + 18, 0, nameY + nameH - 12);
    ink.addColorStop(0, '#ffffff');
    ink.addColorStop(0.6, '#e8f0f8');
    ink.addColorStop(1, '#b7c8d9');
    ctx.fillStyle = ink;
    ctx.shadowColor = R.glow;
    ctx.shadowBlur = 16;
    ctx.fillText(name, W / 2, nameY + nameH / 2 + 2);
    ctx.shadowBlur = 0;
    setSpacing(ctx, 0);

    // ---- Art window (4:3 so the text zones below never collide) ----
    var artX = 86, artY = 186, artW = W - 172, artH = 640;
    ctx.save();
    roundRectPath(ctx, artX - 7, artY - 7, artW + 14, artH + 14, 20);
    ctx.fillStyle = metalGradient(ctx, R.metal, artX, artY, artX + artW, artY + artH);
    ctx.shadowColor = 'rgba(0,0,0,0.45)';
    ctx.shadowBlur = 20;
    ctx.shadowOffsetY = 6;
    ctx.fill();
    ctx.restore();
    roundRectPath(ctx, artX - 7, artY - 7, artW + 14, artH + 14, 20);
    ctx.strokeStyle = 'rgba(0,0,0,0.45)';
    ctx.lineWidth = 2;
    ctx.stroke();
    roundRectPath(ctx, artX, artY, artW, artH, 16);
    ctx.fillStyle = '#050d16';
    ctx.fill();

    ctx.save();
    roundRectPath(ctx, artX, artY, artW, artH, 16);
    ctx.clip();
    if (card.image && card.image.width) {
      // cover-fit
      var iw = card.image.width, ih = card.image.height;
      var s = Math.max(artW / iw, artH / ih);
      var dw = iw * s, dh = ih * s;
      ctx.drawImage(card.image, artX + (artW - dw) / 2, artY + (artH - dh) / 2, dw, dh);
    } else {
      // Placeholder: rink ice + center line
      var ice = ctx.createLinearGradient(0, artY, 0, artY + artH);
      ice.addColorStop(0, '#12314f');
      ice.addColorStop(1, '#0a1f36');
      ctx.fillStyle = ice;
      ctx.fillRect(artX, artY, artW, artH);
      ctx.strokeStyle = 'rgba(255,255,255,0.12)';
      ctx.lineWidth = 10;
      ctx.beginPath();
      ctx.arc(artX + artW / 2, artY + artH / 2, artH * 0.34, 0, Math.PI * 2);
      ctx.stroke();
      ctx.fillStyle = 'rgba(255,255,255,0.25)';
      ctx.font = '700 44px ' + family;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('NO ART', artX + artW / 2, artY + artH / 2);
    }
    // Glass gloss sweeping the top of the art
    var gloss = ctx.createLinearGradient(artX, artY, artX + artW * 0.5, artY + artH * 0.7);
    gloss.addColorStop(0, 'rgba(255,255,255,0.10)');
    gloss.addColorStop(0.4, 'rgba(255,255,255,0.03)');
    gloss.addColorStop(0.7, 'rgba(255,255,255,0)');
    ctx.fillStyle = gloss;
    ctx.fillRect(artX, artY, artW, artH);
    // Inner vignette on art
    var vg = ctx.createRadialGradient(artX + artW / 2, artY + artH / 2, artH * 0.5, artX + artW / 2, artY + artH / 2, artH * 0.85);
    vg.addColorStop(0, 'rgba(0,0,0,0)');
    vg.addColorStop(1, 'rgba(0,0,0,0.38)');
    ctx.fillStyle = vg;
    ctx.fillRect(artX, artY, artW, artH);

    ctx.restore();

    // Rarity glow keyline hugging the art
    ctx.save();
    roundRectPath(ctx, artX + 2, artY + 2, artW - 4, artH - 4, 14);
    ctx.strokeStyle = R.color;
    ctx.globalAlpha = 0.55;
    ctx.lineWidth = 2.5;
    ctx.shadowColor = R.glow;
    ctx.shadowBlur = 14;
    ctx.stroke();
    ctx.restore();

    // ---- Rarity gem, straddling the art's bottom border ----
    var gemY = artY + artH + 4;
    if (!gemArt[rarity]) loadGemArt(rarity);
    var gemEntry = gemArt[rarity];
    if (gemEntry && gemEntry.state === 'ok') {
      var gemHalo = ctx.createRadialGradient(W / 2, gemY, 10, W / 2, gemY, 116);
      gemHalo.addColorStop(0, R.glow);
      gemHalo.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = gemHalo;
      ctx.beginPath();
      ctx.arc(W / 2, gemY, 116, 0, Math.PI * 2);
      ctx.fill();
      // Mythical radiance: light rays fanning out from behind the gem
      if (R.tier >= 5) {
        ctx.save();
        ctx.globalAlpha = 0.55;
        for (var ry = 0; ry < 12; ry++) {
          var ra = ry * Math.PI / 6 + 0.26;
          var rx2 = W / 2 + Math.cos(ra) * 150;
          var ry2 = gemY + Math.sin(ra) * 150 * 0.5;
          var rayG = ctx.createLinearGradient(W / 2, gemY, rx2, ry2);
          rayG.addColorStop(0, 'rgba(255,130,110,0.6)');
          rayG.addColorStop(1, 'rgba(255,130,110,0)');
          ctx.strokeStyle = rayG;
          ctx.lineWidth = ry % 3 === 0 ? 3 : 1.5;
          ctx.beginPath();
          ctx.moveTo(W / 2 + Math.cos(ra) * 52, gemY + Math.sin(ra) * 52 * 0.5);
          ctx.lineTo(rx2, ry2);
          ctx.stroke();
        }
        ctx.restore();
      }
      var gemSize = 132;
      ctx.drawImage(gemEntry.img, W / 2 - gemSize / 2, gemY - gemSize / 2, gemSize, gemSize);
      if (R.tier >= 3) {
        drawSparkle(ctx, W / 2 - 84, gemY - 38, 13, 0.85);
        drawSparkle(ctx, W / 2 + 80, gemY - 48, 9, 0.75);
        if (R.tier >= 4) drawSparkle(ctx, W / 2 + 66, gemY + 44, 8, 0.7);
        if (R.tier >= 5) {
          drawSparkle(ctx, W / 2 - 70, gemY + 50, 11, 0.9);
          drawSparkle(ctx, W / 2, gemY - 72, 15, 0.95);
        }
      }
    } else {
      if (gemEntry && gemEntry.state === 'loading') gemPending.push({ canvas: canvas, card: card, opts: opts });
      drawGem(ctx, W / 2, gemY, 58, rarity);
    }

    // ---- Tags: up to 3 players + a team, below the gem (never over art) ----
    var infoY = gemY + 96;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    var tagNames = (card.tagged_names && card.tagged_names.length
      ? card.tagged_names
      : (card.tagged_name ? [card.tagged_name] : [])).slice(0, 3);
    if (tagNames.length) {
      var namesText = '\u2605 ' + tagNames.join(' \u00b7 ').toUpperCase() + ' \u2605';
      setSpacing(ctx, 2);
      var namesPx = fitFont(ctx, namesText, '700', 32, 20, W - 190, family);
      ctx.font = '700 ' + namesPx + 'px ' + family;
      ctx.fillStyle = R.color;
      ctx.shadowColor = 'rgba(0,0,0,0.6)';
      ctx.shadowBlur = 6;
      ctx.fillText(namesText, W / 2, infoY);
      ctx.shadowBlur = 0;
      setSpacing(ctx, 0);
      infoY += 44;
    }
    if (card.tagged_team) {
      setSpacing(ctx, 4);
      ctx.font = '700 25px ' + family;
      ctx.fillStyle = 'rgba(188,203,218,0.8)';
      ctx.fillText(String(card.tagged_team).toUpperCase(), W / 2, infoY);
      setSpacing(ctx, 0);
      infoY += 40;
    }

    // ---- Description: auto-sized + vertically centered in its zone ----
    // Big and bold when it's one punchy line, shrinking (never below
    // 26px) as the text grows; the block always sits centered between
    // the tags and the flavor divider (or the mint row when no flavor).
    var zoneTop = Math.max(infoY + 8, gemY + 104);
    var zoneBottom = card.flavor_text ? (H - 214) : (H - 124);
    var desc = card.description || '';
    if (desc && zoneBottom > zoneTop) {
      var zoneH = zoneBottom - zoneTop;
      var fitted = null;
      for (var descPx = 42; descPx >= 26; descPx -= 2) {
        ctx.font = '400 ' + descPx + 'px ' + family;
        var tryLines = wrapText(ctx, desc, W - 220);
        var tryLh = Math.round(descPx * 1.32);
        if (tryLines.length * tryLh <= zoneH) {
          fitted = { px: descPx, lines: tryLines, lh: tryLh };
          break;
        }
      }
      if (!fitted) {
        // Even 26px overflows: keep 26px and truncate with an ellipsis
        ctx.font = '400 26px ' + family;
        var minLines = wrapText(ctx, desc, W - 220);
        var minLh = Math.round(26 * 1.32);
        var maxLines = Math.max(1, Math.floor(zoneH / minLh));
        if (minLines.length > maxLines) {
          minLines = minLines.slice(0, maxLines);
          minLines[maxLines - 1] = minLines[maxLines - 1].replace(/\s*\S*$/, '') + '\u2026';
        }
        fitted = { px: 26, lines: minLines, lh: minLh };
      }
      ctx.font = '400 ' + fitted.px + 'px ' + family;
      ctx.fillStyle = '#dce7f1';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      var blockH = fitted.lines.length * fitted.lh;
      var lineY = zoneTop + (zoneH - blockH) / 2 + fitted.lh / 2;
      for (var i = 0; i < fitted.lines.length; i++) {
        ctx.fillText(fitted.lines[i], W / 2, lineY + i * fitted.lh);
      }
    }
    ctx.textBaseline = 'top'; // the flavor block below expects top baseline

    // ---- Flavor text (italic, bottom) ----
    var flavorY = H - 176;
    if (card.flavor_text) {
      var fl = ctx.createLinearGradient(180, 0, W - 180, 0);
      fl.addColorStop(0, 'rgba(255,255,255,0)');
      fl.addColorStop(0.5, 'rgba(255,255,255,0.22)');
      fl.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.strokeStyle = fl;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(180, flavorY - 22);
      ctx.lineTo(W / 2 - 16, flavorY - 22);
      ctx.moveTo(W / 2 + 16, flavorY - 22);
      ctx.lineTo(W - 180, flavorY - 22);
      ctx.stroke();
      drawDiamond(ctx, W / 2, flavorY - 22, 6, 'rgba(255,255,255,0.35)');
      ctx.font = 'italic 300 31px ' + family;
      ctx.fillStyle = 'rgba(196,210,224,0.88)';
      var flavLines = wrapText(ctx, '“' + card.flavor_text + '”', W - 260).slice(0, 2);
      for (var f = 0; f < flavLines.length; f++) {
        ctx.fillText(flavLines[f], W / 2, flavorY + f * 40);
      }
    }

    // ---- Bottom strip: mint number pill + set ----
    ctx.textBaseline = 'middle';
    ctx.font = '700 26px ' + family;
    var mintText = card.mint_number ? 'MINT #' + card.mint_number : 'LIHL';
    var mintW = ctx.measureText(mintText).width;
    var pillY = H - 66;
    roundRectPath(ctx, 78, pillY - 22, mintW + 44, 44, 22);
    ctx.fillStyle = 'rgba(255,255,255,0.05)';
    ctx.fill();
    ctx.strokeStyle = 'rgba(160,178,196,0.45)';
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.textAlign = 'left';
    ctx.fillStyle = '#ccd9e5';
    ctx.fillText(mintText, 100, pillY + 1);
    ctx.textAlign = 'right';
    ctx.fillStyle = 'rgba(160,178,196,0.9)';
    setSpacing(ctx, 2);
    ctx.fillText('LIHL SERIES 1', W - 78, pillY + 1);
    setSpacing(ctx, 0);
    drawDiamond(ctx, W - 78 - ctx.measureText('LIHL SERIES 1').width - 26, pillY, 5, R.color);
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    return canvas;
  }

  // ---------- back ----------

  // The card back is the LIHL shield artwork (website/assets/card-back.jpg,
  // 1024x1434). It loads once per page; canvases rendered before it arrives
  // get the procedural back below and are repainted when it lands.
  var BACK_ART_URL = 'assets/card-back.jpg';
  var backArtImg = null;
  var backArtState = 'idle'; // idle | loading | ok | error
  var backArtPending = [];

  function loadBackArt() {
    backArtState = 'loading';
    var img = new Image();
    img.onload = function () {
      backArtImg = img;
      backArtState = 'ok';
      var redo = backArtPending;
      backArtPending = [];
      redo.forEach(function (job) { renderBack(job.canvas, job.opts); });
    };
    img.onerror = function () {
      backArtState = 'error'; // procedural fallback stays
      backArtPending = [];
    };
    img.src = BACK_ART_URL;
  }

  function renderBack(canvas, opts) {
    opts = opts || {};
    var scale = opts.pixelRatio || 1;
    canvas.width = W * scale;
    canvas.height = H * scale;
    var ctx = canvas.getContext('2d');
    ctx.setTransform(scale, 0, 0, scale, 0, 0);

    if (backArtState === 'idle' && typeof Image !== 'undefined') loadBackArt();
    if (backArtState === 'ok') {
      ctx.clearRect(0, 0, W, H);
      ctx.save();
      roundRectPath(ctx, 0, 0, W, H, 56);
      ctx.clip();
      // cover-fit (the asset is already exactly W x H)
      var s = Math.max(W / backArtImg.width, H / backArtImg.height);
      var dw = backArtImg.width * s, dh = backArtImg.height * s;
      ctx.imageSmoothingQuality = 'high';
      ctx.drawImage(backArtImg, (W - dw) / 2, (H - dh) / 2, dw, dh);
      ctx.restore();
      return canvas;
    }
    if (backArtState === 'loading') backArtPending.push({ canvas: canvas, opts: opts });

    var family = "'Roboto', 'Helvetica Neue', Arial, sans-serif";
    var pad = 30;

    // Frame — neutral steel with a faint cool foil sweep
    var steel = { metal: ['#3a4c60', '#16232f', '#2c3d4f', '#101c28', '#33455a'],
                  foil: ['rgba(111,211,255,0.10)', 'rgba(150,170,255,0.08)', 'rgba(111,211,255,0.10)'],
                  tier: 0 };
    drawFrame(ctx, steel, pad);

    roundRectPath(ctx, pad, pad, W - pad * 2, H - pad * 2, 36);
    var body = ctx.createLinearGradient(0, 0, W, H);
    body.addColorStop(0, '#0c2038');
    body.addColorStop(0.5, '#081524');
    body.addColorStop(1, '#0c2038');
    ctx.fillStyle = body;
    ctx.fill();

    ctx.save();
    roundRectPath(ctx, pad, pad, W - pad * 2, H - pad * 2, 36);
    ctx.clip();

    // Fine diagonal pinstripes + grain, matching the face body
    ctx.strokeStyle = 'rgba(150,190,230,0.04)';
    ctx.lineWidth = 1;
    for (var px = -H; px < W; px += 18) {
      ctx.beginPath();
      ctx.moveTo(px, 0);
      ctx.lineTo(px + H, H);
      ctx.stroke();
    }
    var np = noisePattern(ctx);
    if (np) {
      ctx.globalAlpha = 0.5;
      ctx.fillStyle = np;
      ctx.fillRect(pad, pad, W - pad * 2, H - pad * 2);
      ctx.globalAlpha = 1;
    }

    // Radial ice burst
    var cx = W / 2, cy = H / 2;
    for (var i = 0; i < 28; i++) {
      var a = i * Math.PI * 2 / 28;
      var ray = ctx.createLinearGradient(cx, cy, cx + Math.cos(a) * H, cy + Math.sin(a) * H);
      ray.addColorStop(0, i % 2 ? 'rgba(55,165,255,0.18)' : 'rgba(55,165,255,0.10)');
      ray.addColorStop(1, 'rgba(55,165,255,0)');
      ctx.strokeStyle = ray;
      ctx.lineWidth = i % 2 ? 3 : 7;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(cx + Math.cos(a) * H, cy + Math.sin(a) * H);
      ctx.stroke();
    }

    // Concentric rings with tick marks
    ctx.strokeStyle = 'rgba(111,211,255,0.25)';
    ctx.lineWidth = 4;
    [150, 260, 380].forEach(function (r) {
      ctx.beginPath();
      ctx.arc(cx, cy, r, 0, Math.PI * 2);
      ctx.stroke();
    });
    ctx.strokeStyle = 'rgba(111,211,255,0.35)';
    ctx.lineWidth = 3;
    for (var t = 0; t < 36; t++) {
      var ta = t * Math.PI / 18;
      ctx.beginPath();
      ctx.moveTo(cx + Math.cos(ta) * 372, cy + Math.sin(ta) * 372);
      ctx.lineTo(cx + Math.cos(ta) * 388, cy + Math.sin(ta) * 388);
      ctx.stroke();
    }

    // Center puck with beveled ring
    ctx.beginPath();
    ctx.arc(cx, cy, 196, 0, Math.PI * 2);
    ctx.strokeStyle = metalGradient(ctx, steel.metal, cx - 200, cy - 200, cx + 200, cy + 200);
    ctx.lineWidth = 12;
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(cx, cy, 190, 0, Math.PI * 2);
    var puck = ctx.createRadialGradient(cx - 60, cy - 70, 20, cx, cy, 200);
    puck.addColorStop(0, '#1d3a5c');
    puck.addColorStop(1, '#060e18');
    ctx.fillStyle = puck;
    ctx.fill();
    ctx.strokeStyle = 'rgba(111,211,255,0.6)';
    ctx.lineWidth = 4;
    ctx.stroke();

    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    setSpacing(ctx, 4);
    ctx.font = '900 118px ' + family;
    ctx.fillStyle = '#eaf4fc';
    ctx.shadowColor = 'rgba(55,165,255,0.8)';
    ctx.shadowBlur = 30;
    ctx.fillText('LIHL', cx, cy - 22);
    ctx.shadowBlur = 0;
    setSpacing(ctx, 6);
    ctx.font = '700 34px ' + family;
    ctx.fillStyle = '#6fd3ff';
    ctx.fillText('TRADING CARDS', cx, cy + 66);

    ctx.font = '700 30px ' + family;
    ctx.fillStyle = 'rgba(160,178,196,0.75)';
    ctx.fillText('LONG ISLAND HOCKEY LEAGUE', cx, H - 120);
    setSpacing(ctx, 3);
    ctx.font = '400 26px ' + family;
    ctx.fillText('SERIES 1', cx, H - 76);
    setSpacing(ctx, 0);

    ctx.restore();
    return canvas;
  }

  return { W: W, H: H, RARITY: RARITY, renderFace: renderFace, renderBack: renderBack };
}));
